import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { InvoiceProvider, useInvoice } from '@/contexts/InvoiceContext'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { useInvoiceOperations } from '@/hooks/useInvoiceOperations'
import { Invoice, EmailTemplate } from '@/types/invoice'
import { Dropzone } from '@/components/ui/dropzone'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Archive, LogOut, Users, FileText, Type } from 'lucide-react'
import {
  LazyWrapper,
  LazyAnalyticsDashboard,
  LazyResponsiveInvoiceList,
  LazyUserManagement,
  AnalyticsDashboardSkeleton,
  InvoiceListSkeleton,
} from '@/components/LazyWrapper'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { EmailTemplateEditor } from '@/components/EmailTemplateEditor'
import { EmailPreview } from '@/components/EmailPreview'
import { ArchiveDialog } from '@/components/ArchiveDialog'
import { ArchiveHistory } from '@/components/ArchiveHistory'
import { LoginForm } from '@/components/LoginForm'
import { motion, AnimatePresence } from 'framer-motion'
import { PageTransition } from '@/components/PageTransition'

function AppContent() {
  const { state } = useInvoice()
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth()
  const {
    parseFile,
    parseText,
    loadInvoices,
    loadEmailTemplates,
    generateEmailContent,
    openEmail,
    archiveInvoice,
    restoreInvoice,
    isLoading,
    error,
  } = useInvoiceOperations()
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>(
    state.invoices
  )
  const [activeTab, setActiveTab] = useState<'file' | 'text'>('text')
  const [textInput, setTextInput] = useState('')
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [archiveInvoiceData, setArchiveInvoiceData] = useState<Invoice | null>(
    null
  )
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [activeView, setActiveView] = useState<'main' | 'users'>('main')

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      await parseFile(files)
    },
    [parseFile]
  )

  const handleTextSubmit = useCallback(
    async (text: string) => {
      console.log('App handleTextSubmit called', { text })
      await parseText(text)
    },
    [parseText]
  )

  const handleFilteredInvoices = useCallback((invoices: Invoice[]) => {
    setFilteredInvoices(invoices)
  }, [])

  const handlePreviewEmail = useCallback((invoice: Invoice) => {
    setPreviewInvoice(invoice)
    setIsPreviewOpen(true)
  }, [])

  const handleOpenEmail = useCallback(
    (invoice: Invoice) => {
      openEmail(invoice, state.emailTemplate)
    },
    [openEmail, state.emailTemplate]
  )

  const handlePreviewTemplate = useCallback(
    (template: EmailTemplate) => {
      if (previewInvoice) {
        // Update the preview with the new template
        setIsPreviewOpen(false)
        setTimeout(() => {
          setPreviewInvoice(previewInvoice)
          setIsPreviewOpen(true)
        }, 100)
      }
    },
    [previewInvoice]
  )

  const handleArchiveInvoice = useCallback((invoice: Invoice) => {
    setArchiveInvoiceData(invoice)
    setIsArchiveOpen(true)
  }, [])

  const handleArchiveConfirm = useCallback(
    async (notes?: string) => {
      if (archiveInvoiceData) {
        await archiveInvoice(archiveInvoiceData, state.emailTemplate, notes)
      }
    },
    [archiveInvoiceData, archiveInvoice, state.emailTemplate]
  )

  // Load invoices and email templates from database on component mount
  useEffect(() => {
    if (isAuthenticated) {
      loadInvoices()
      loadEmailTemplates()
      // Ensure we start on the main view when logged in
      setActiveView('main')
    } else {
      // Reset to main view when not authenticated
      setActiveView('main')
    }
  }, [isAuthenticated, loadInvoices, loadEmailTemplates])

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm />
  }

  return (
    <ErrorBoundary>
      <PageTransition>
        <div className="min-h-screen bg-background">
          <div className="container mx-auto py-8 px-4">
            <div className="max-w-6xl mx-auto space-y-8">
              {/* Header */}
              <div className="flex justify-between items-center">
                <div className="text-center space-y-4 flex-1">
                  <h1 className="text-4xl font-bold">
                    Invoice Parser & Analytics
                  </h1>
                  <p className="text-xl text-muted-foreground">
                    Upload invoice files and analyze overdue invoices
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      Welcome, {user?.username}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {user?.role}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {user?.role === 'admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setActiveView(
                            activeView === 'main' ? 'users' : 'main'
                          )
                        }
                        className="flex items-center gap-2"
                      >
                        <Users className="w-4 h-4" />
                        {activeView === 'main' ? 'Users' : 'Main'}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={logout}
                      className="flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </Button>
                  </div>
                </div>
              </div>

              {/* Main Content or User Management */}
              {activeView === 'main' ? (
                <>
                  {/* Input Options */}
                  <div className="space-y-4">
                    <h2 className="text-2xl font-semibold">Add Invoice Data</h2>

                    {/* Enhanced Tab Navigation with shadcn Tabs */}
                    <Tabs
                      value={activeTab}
                      onValueChange={(value: string) =>
                        setActiveTab(value as 'file' | 'text')
                      }
                      className="w-full"
                    >
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger
                          value="file"
                          className="flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Upload File
                        </TabsTrigger>
                        <TabsTrigger
                          value="text"
                          className="flex items-center gap-2"
                        >
                          <Type className="w-4 h-4" />
                          Paste Text
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="file" className="space-y-4 mt-6">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Dropzone
                            onDrop={handleFileUpload}
                            disabled={isLoading}
                          />
                          {!isLoading && (
                            <div className="text-center text-sm text-muted-foreground mt-4">
                              <p>Supported formats: PDF, TXT, JSON</p>
                              <p>Maximum file size: 10MB</p>
                            </div>
                          )}
                        </motion.div>
                      </TabsContent>

                      <TabsContent value="text" className="space-y-4 mt-6">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-4"
                        >
                          <div className="space-y-2">
                            <Label
                              htmlFor="invoice-text"
                              className="text-lg font-semibold"
                            >
                              Paste Invoice Text
                            </Label>
                            <textarea
                              id="invoice-text"
                              value={textInput}
                              onChange={(e) => setTextInput(e.target.value)}
                              className="w-full min-h-[200px] p-4 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                              placeholder="Paste your invoice text here..."
                              disabled={isLoading}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {textInput.length} characters
                              </Badge>
                              {textInput.length > 0 && textInput.length < 5 && (
                                <Badge variant="warning" className="text-xs">
                                  Minimum 5 characters required
                                </Badge>
                              )}
                            </div>
                            <Button
                              onClick={() => handleTextSubmit(textInput)}
                              disabled={
                                isLoading ||
                                !textInput.trim() ||
                                textInput.trim().length < 5
                              }
                              className="px-6"
                            >
                              {isLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                  Processing...
                                </>
                              ) : (
                                'Parse Text'
                              )}
                            </Button>
                          </div>
                        </motion.div>
                      </TabsContent>
                    </Tabs>

                    {isLoading && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-8"
                      >
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                        <p className="text-muted-foreground">
                          {activeTab === 'file'
                            ? 'Processing file...'
                            : 'Processing text...'}
                        </p>
                      </motion.div>
                    )}
                  </div>

                  {/* Email Template Manager */}

                  {/* Email Template Editor */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-semibold">Email Template</h2>
                      <Button
                        onClick={() => setIsHistoryOpen(true)}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Archive className="w-4 h-4" />
                        View Archive ({state.archive.length})
                      </Button>
                    </div>
                    {state.invoices.length > 0 && (
                      <EmailTemplateEditor
                        onPreview={handlePreviewTemplate}
                        onOpenEmail={() => {}}
                      />
                    )}
                  </div>

                  {/* Analytics Dashboard */}
                  {state.invoices.length > 0 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold">
                        Analytics Dashboard
                      </h2>
                      <LazyWrapper fallback={<AnalyticsDashboardSkeleton />}>
                        <LazyAnalyticsDashboard invoices={state.invoices} />
                      </LazyWrapper>
                    </div>
                  )}

                  {/* Responsive Invoice List */}
                  {state.invoices.length > 0 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold">
                        Detailed Invoice Overview
                      </h2>

                      {/* Enhanced Summary Statistics with Animation */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, staggerChildren: 0.1 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-4"
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 }}
                          className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-xl"
                        >
                          <h3 className="text-sm font-semibold text-blue-800 mb-1">
                            Total Invoices
                          </h3>
                          <p className="text-2xl font-bold text-blue-600">
                            {state.invoices.length}
                          </p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 }}
                          className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-4 rounded-xl"
                        >
                          <h3 className="text-sm font-semibold text-green-800 mb-1">
                            Total Amount
                          </h3>
                          <p className="text-2xl font-bold text-green-600">
                            {state.invoices
                              .reduce((sum, inv) => sum + inv.amount, 0)
                              .toLocaleString('fr-FR', {
                                style: 'currency',
                                currency: 'EUR',
                              })}
                          </p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 }}
                          className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 p-4 rounded-xl"
                        >
                          <h3 className="text-sm font-semibold text-orange-800 mb-1">
                            Overdue Count
                          </h3>
                          <p className="text-2xl font-bold text-orange-600">
                            {
                              state.invoices.filter((inv) => {
                                const dueDate = new Date(inv.dueDate)
                                const today = new Date()
                                return dueDate < today
                              }).length
                            }
                          </p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.4 }}
                          className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 p-4 rounded-xl"
                        >
                          <h3 className="text-sm font-semibold text-red-800 mb-1">
                            Overdue Amount
                          </h3>
                          <p className="text-2xl font-bold text-red-600">
                            {state.invoices
                              .filter((inv) => {
                                const dueDate = new Date(inv.dueDate)
                                const today = new Date()
                                return dueDate < today
                              })
                              .reduce((sum, inv) => sum + inv.amount, 0)
                              .toLocaleString('fr-FR', {
                                style: 'currency',
                                currency: 'EUR',
                              })}
                          </p>
                        </motion.div>
                      </motion.div>

                      {/* New Responsive Invoice List */}
                      <LazyWrapper fallback={<InvoiceListSkeleton />}>
                        <LazyResponsiveInvoiceList
                          invoices={state.invoices}
                          onPreviewEmail={handlePreviewEmail}
                          onSendEmail={handleOpenEmail}
                          onCopyEmail={() => {}} // Add copy functionality if needed
                          onArchiveInvoice={handleArchiveInvoice}
                          loading={isLoading}
                        />
                      </LazyWrapper>
                    </div>
                  )}
                </>
              ) : (
                <LazyWrapper>
                  <LazyUserManagement />
                </LazyWrapper>
              )}
            </div>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'hsl(var(--background))',
                  color: 'hsl(var(--foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
          </div>

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'hsl(var(--background))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
              },
            }}
          />
        </div>
      </PageTransition>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <AuthProvider>
      <InvoiceProvider>
        <AppContent />
      </InvoiceProvider>
    </AuthProvider>
  )
}

export default App
